import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { formatFollowerCount } from '../utils/formatFollowers';
import "../css/SearchPage.css";

const getSearchStorageKey = () => {
  try {
    const u = JSON.parse(localStorage.getItem('user') || 'null');
    return (u && u._id) ? `persistent_search_state_${u._id}` : 'persistent_search_state';
  } catch (e) {
    return 'persistent_search_state';
  }
};

const getSavedSearchState = () => {
  try {
    const key = getSearchStorageKey();
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed && Array.isArray(parsed.users)) {
        return parsed;
      }
    }
  } catch (e) {}
  return null;
};

export default function SearchPage() {
  const navigate = useNavigate();
  const restoredStateRef = useRef(getSavedSearchState());
  const restored = restoredStateRef.current;
  const restoredScrollY = (restored && typeof restored.scrollY === 'number' && restored.scrollY > 0) ? restored.scrollY : 0;

  const [users, setUsers] = useState(() => (restored && Array.isArray(restored.users) ? restored.users : []));
  const [query, setQuery] = useState(() => (restored && typeof restored.query === 'string' ? restored.query : ""));
  const [interestQuery, setInterestQuery] = useState(() => {
    if (restored && typeof restored.interestQuery === 'string') return restored.interestQuery;
    try { const u = JSON.parse(localStorage.getItem('user') || 'null'); return (u && u.interests) ? u.interests : ''; } catch (e) { return ''; }
  });
  const [aboutQuery, setAboutQuery] = useState(() => (restored && typeof restored.aboutQuery === 'string' ? restored.aboutQuery : ""));
  const [showFilterRequiredMessage, setShowFilterRequiredMessage] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(() => (restored && typeof restored.hasMore === 'boolean' ? restored.hasMore : true));
  const [showFilterBar, setShowFilterBar] = useState(true);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const lastScrollYRef = useRef(0);
  const [isMobile, setIsMobile] = useState(false);
  const skipRef = useRef(restored && typeof restored.skip === 'number' ? restored.skip : 0);
  const loadingRef = useRef(false);
  const debounceRef = useRef(null);
  const requestIdRef = useRef(0);
  const isFirstRender = useRef(true);
  const isRestoringRef = useRef(restoredScrollY > 0);
  const limit = 5;

  const stateRef = useRef({
    users: (restored && Array.isArray(restored.users)) ? restored.users : [],
    skip: (restored && typeof restored.skip === 'number') ? restored.skip : 0,
    hasMore: (restored && typeof restored.hasMore === 'boolean') ? restored.hasMore : true,
    query: (restored && typeof restored.query === 'string') ? restored.query : '',
    interestQuery: (restored && typeof restored.interestQuery === 'string') ? restored.interestQuery : '',
    aboutQuery: (restored && typeof restored.aboutQuery === 'string') ? restored.aboutQuery : '',
    scrollY: restoredScrollY
  });

  // Keep stateRef in sync with React state
  stateRef.current.users = users;
  stateRef.current.skip = skipRef.current;
  stateRef.current.hasMore = hasMore;
  stateRef.current.query = query;
  stateRef.current.interestQuery = interestQuery;
  stateRef.current.aboutQuery = aboutQuery;

  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const highlightText = (text, tokens) => {
    if (!text) return text;
    const toks = (Array.isArray(tokens) ? tokens : [tokens]).filter(Boolean).map(t => t.trim()).filter(Boolean);
    if (!toks.length) return text;
    const pattern = toks.map(escapeRegExp).join('|');
    const re = new RegExp(`(${pattern})`, 'gi');
    const parts = text.split(re);
    return parts.map((part, idx) => {
      if (re.test(part)) {
        return <span key={idx} className="hl">{part}</span>;
      }
      return <span key={idx}>{part}</span>;
    });
  };

  const persistState = () => {
    try {
      const key = getSearchStorageKey();
      const currentScroll = isRestoringRef.current ? restoredScrollY : (stateRef.current.scrollY || window.scrollY || document.documentElement.scrollTop || 0);
      const dataToSave = {
        users: stateRef.current.users,
        skip: skipRef.current,
        hasMore: stateRef.current.hasMore,
        query: stateRef.current.query,
        interestQuery: stateRef.current.interestQuery,
        aboutQuery: stateRef.current.aboutQuery,
        scrollY: currentScroll
      };
      if (dataToSave.users.length > 0 || dataToSave.query || dataToSave.interestQuery || dataToSave.aboutQuery) {
        localStorage.setItem(key, JSON.stringify(dataToSave));
      }
    } catch (e) {
      console.error("Error saving search state:", e);
    }
  };

  // 1. Manual scroll restoration on mount with requestAnimationFrame retry loop
  useEffect(() => {
    try {
      if ('scrollRestoration' in window.history) {
        window.history.scrollRestoration = 'manual';
      }
    } catch (e) {}

    if (restoredScrollY <= 0) {
      isRestoringRef.current = false;
      return;
    }

    let attempts = 0;
    const maxAttempts = 30;
    let animationFrameId = null;

    const performScroll = () => {
      window.scrollTo(0, restoredScrollY);
      const currentY = window.scrollY || document.documentElement.scrollTop || 0;
      attempts++;

      if (Math.abs(currentY - restoredScrollY) < 15 || attempts >= maxAttempts) {
        window.scrollTo(0, restoredScrollY);
        setTimeout(() => {
          isRestoringRef.current = false;
        }, 100);
      } else {
        animationFrameId = requestAnimationFrame(performScroll);
      }
    };

    animationFrameId = requestAnimationFrame(performScroll);

    const safetyTimer = setTimeout(() => {
      window.scrollTo(0, restoredScrollY);
      isRestoringRef.current = false;
    }, 1200);

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
      clearTimeout(safetyTimer);
    };
  }, []);

  // 2. Track scroll position continuously
  useEffect(() => {
    let scrollTimeout = null;
    const handleScrollEvent = () => {
      const currentY = window.scrollY || document.documentElement.scrollTop || 0;
      setShowScrollTop(currentY > 200);

      if (isRestoringRef.current) return;
      stateRef.current.scrollY = currentY;
      if (scrollTimeout) clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(persistState, 150);
    };

    window.addEventListener('scroll', handleScrollEvent, { passive: true });
    return () => {
      if (scrollTimeout) clearTimeout(scrollTimeout);
      window.removeEventListener('scroll', handleScrollEvent);
    };
  }, []);

  // 3. Save state on visibilitychange / beforeunload / unmount
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        persistState();
      }
    };
    const onBeforeUnload = () => {
      persistState();
    };
    window.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('beforeunload', onBeforeUnload);
      persistState();
    };
  }, []);

  // 4. Save state whenever search results update
  useEffect(() => {
    persistState();
  }, [users, hasMore]);

  // 5. Query filter updates
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      if (restored && restored.users && restored.users.length > 0) {
        // Skip initial fetch on mount because we already hydrated users from localStorage
        return;
      }
    }

    if (!interestQuery && !aboutQuery && !query) {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setUsers([]);
      skipRef.current = 0;
      setHasMore(true);
      setShowFilterRequiredMessage(true);
      stateRef.current.scrollY = 0;
      persistState();
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setUsers([]);
      skipRef.current = 0;
      setHasMore(true);
      setShowFilterRequiredMessage(false);
      stateRef.current.scrollY = 0;
      fetchUsers(true);
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [interestQuery, aboutQuery, query]);

  const fetchUsers = (reset = false) => {
    if (!reset && loadingRef.current) return;
    const myRequestId = ++requestIdRef.current;
    loadingRef.current = true;
    setLoading(true);

    if (!interestQuery && !aboutQuery && !query) {
      setUsers([]);
      setShowFilterRequiredMessage(true);
      loadingRef.current = false;
      setLoading(false);
      return;
    }

    const currentSkip = reset ? 0 : skipRef.current;
    const queryString = new URLSearchParams({
      interestQuery,
      query,
      aboutQuery,
      limit: limit.toString(),
      skip: currentSkip.toString()
    }).toString();

    fetch(`/search?${queryString}`, {
      headers: {
        Authorization: "Bearer " + localStorage.getItem("jwt"),
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error(`Error: ${res.status}`);
        return res.json();
      })
      .then((result) => {
        const received = result || [];
        if (myRequestId !== requestIdRef.current) {
          loadingRef.current = false;
          setLoading(false);
          return;
        }

        setHasMore(received.length === limit);

        setUsers(prev => {
          if (reset) return received;
          const existingIds = new Set(prev.map(u => u._id));
          const filtered = received.filter(u => !existingIds.has(u._id));
          return [...prev, ...filtered];
        });

        skipRef.current = reset ? received.length : (skipRef.current + received.length);
        loadingRef.current = false;
        setLoading(false);
        setShowFilterRequiredMessage(false);
      })
      .catch((err) => {
        console.log("Fetch error: ", err);
        loadingRef.current = false;
        setLoading(false);
      });
  };

  const handleScroll = () => {
    if (loadingRef.current) return;
    if (!hasMore) return;
    if (window.innerHeight + document.documentElement.scrollTop >= document.documentElement.offsetHeight - 120) {
      fetchUsers();
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const updateMobileStatus = () => {
    setIsMobile(window.innerWidth <= 768);
  };

  const controlFilterBar = () => {
    if (isRestoringRef.current) return;
    if (window.scrollY > lastScrollYRef.current) {
      setShowFilterBar(false);
    } else {
      setShowFilterBar(true);
    }
    lastScrollYRef.current = window.scrollY;
  };

  useEffect(() => {
    updateMobileStatus();
    lastScrollYRef.current = window.scrollY || 0;
    setShowFilterBar(true);
    window.addEventListener('resize', updateMobileStatus);
    window.addEventListener('scroll', controlFilterBar);
    return () => {
      window.removeEventListener('resize', updateMobileStatus);
      window.removeEventListener('scroll', controlFilterBar);
    };
  }, []);

  const handleInputChange = (e) => {
    setQuery(e.target.value);
  };

  const handleInterestQueryChange = (e) => {
    setInterestQuery(e.target.value);
  };

  const handleUserClick = (userId) => {
    const currentY = window.scrollY || document.documentElement.scrollTop || 0;
    stateRef.current.scrollY = currentY;
    persistState();
    navigate(`/profile/${userId}`);
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    stateRef.current.scrollY = 0;
    persistState();
  };

  return (
    <div className='search-page'>
      <div className={`search-container ${showFilterBar ? 'show' : 'hide'}`}>
        <input 
          type="text" 
          placeholder="Search by username" 
          value={query} 
          onChange={handleInputChange} 
          className="search-input"
          style={{padding:'6px 8px', "height":"23px", "fontWeight":"600", fontSize:14, "width": "93%", "margin": "auto"}}
          maxLength={120}
        />
        <input 
          type="text" 
          placeholder="Search by interest => music, sports.." 
          value={interestQuery}
          onChange={handleInterestQueryChange} 
          className="search-input"
          style={{padding:'6px 8px', "height":"23px", "fontWeight":"600", fontSize:14, "width": "93%", "margin": "auto"}}
          maxLength={80}
        />
        <input 
          type="text"
          placeholder="Search by About => developer, designer.."
          value={aboutQuery}
          onChange={(e) => setAboutQuery(e.target.value)}
          className="search-input"
          style={{padding:'6px 8px', "height":"23px", "fontWeight":"600", fontSize:14, "width": "93%", "margin": "auto"}}
          maxLength={128}
        />
          {showFilterRequiredMessage && (
            <div style={{color:'#666', padding:'8px 0'}}>Atleast fill any one filter</div>
          )}
      </div>
      <div className="suggestions-container">
        {users.map((user) => (
          <div 
            key={user._id} 
            className="suggestion" 
            onClick={() => handleUserClick(user._id)}
          >
            <img src={user.Photo ? user.Photo : "https://cdn-icons-png.flaticon.com/128/1177/1177568.png"} alt="" />
            <div className="user-info">
              <h5>{highlightText(user.name, query)}</h5>
              <p>{highlightText(user.userName, query)}</p>
              <p>{highlightText(user.interests || '', interestQuery ? interestQuery.split(',') : [])}</p>
              <p>{formatFollowerCount(user.followers ? user.followers.length : 0)} followers</p>
            </div>
          </div>
        ))}
        {loading && (
          <div style={{display:'flex',justifyContent:'center',padding:16}} className="infinite-spinner">
            <div className="spinner" aria-hidden="true"></div>
          </div>
        )}
      </div>

      {showScrollTop && (
        <button
          className="scroll-to-top-btn"
          onClick={scrollToTop}
          aria-label="Scroll to top"
          title="Scroll to top"
        >
          <span className="material-symbols-outlined" style={{ fontSize: "28px" }}>
            arrow_upward
          </span>
        </button>
      )}
    </div>
  );
}
